import { browseLayoutTabBarIcon } from "@-/dev.improve.haniverse.routes.nav.app.browse.tabs._components.tab-bar-icon";
import charactersTabIcon from "@-/dev.improve.haniverse.assets.tab-icons/@images/characters.png";
import primitivesTabIcon from "@-/dev.improve.haniverse.assets.tab-icons/@images/primitives.png";
import wordsTabIcon from "@-/dev.improve.haniverse.assets.tab-icons/@images/words.png";
import { BrowseMaterialTabBar } from "@-/dev.improve.haniverse.routes.nav.app.browse.tabs._components.tab-bar";
import { NativeTabs } from "@-/com.npmjs.expo-router/native-tabs";
import { useCSSVariable, useUniwind } from "@-/com.npmjs.uniwind";
import { useEffect } from "react";
import { subscribeBrowseReset } from "@-/dev.improve.haniverse.routes.nav._lib.primary-navigation-strip";
import { useBrowseLevelsPageNavigator } from "@-/dev.improve.haniverse.routes.nav.app.browse.tabs.levels.navigator";
import {
  PRIMARY_NAVIGATION_STRIP_FADE_INSET_PX,
  FLOATING_TAB_BAR_BOTTOM_GAP_PX,
  usePrimaryNavigationStripFadeReach,
  usePrimaryNavigationStripFloatingTabs,
} from "@-/dev.improve.haniverse.lib.primary-navigation-strip";
import { useIsNavigationFocused } from "@-/dev.improve.haniverse.contexts.primary-controls-strip.opaque-chrome";
import {
  appendMissingPlaceholderTabDescriptors,
  appendMissingPlaceholderTabRoutes,
  createStandardMaterialTopTabNavigator,
  type MaterialTopTabNavigationOptions,
  type MaterialTopTabNavigationEventMap,
  type MaterialTopTabNavigationConfig,
} from "@-/com.npmjs.expo-router/material-top-tabs";
import type {
  ParamListBase,
  TabNavigationState,
  TabRouterOptions,
} from "@-/com.npmjs.expo-router/react-navigation";
import { Platform, View } from "@-/com.npmjs.react-native";
import {
  createBaseTabProps,
  integrateWithRouter,
  TabRouter,
  type RoutePath,
} from "@-/com.npmjs.expo-router";
import path from "@-/com.npmjs.pathe";
import { useSetParentBrowseTab } from "@-/dev.improve.haniverse.routes.nav.app.browse._context";
import { LevelSurfacesLoadingPage } from "@-/dev.improve.haniverse.routes.nav.app._components.level.surface";
import {
  BrowsePrimitivesListSkeleton,
  BrowseWordsSkeleton,
} from "@-/dev.improve.haniverse.routes.nav.app.browse.tabs._components.loading-skeletons";

const CSS_COLOR_PREFIX_PATTERN = /^(#|rgb|hsl)/;

function createMaterialTopTabsProps(dependencies: Parameters<typeof createBaseTabProps>[0]) {
  return {
    ...createBaseTabProps(dependencies),
    navigateToTabSync: (name: string, params: object | undefined) =>
      dependencies.dispatchSync({ payload: { name, params }, type: "JUMP_TO" }),
  };
}

const MaterialTopTabs = integrateWithRouter<
  MaterialTopTabNavigationOptions,
  TabNavigationState<ParamListBase>,
  MaterialTopTabNavigationEventMap,
  MaterialTopTabNavigationConfig,
  TabRouterOptions,
  ReturnType<typeof createMaterialTopTabsProps>
>(createStandardMaterialTopTabNavigator, TabRouter, {
  createProps: createMaterialTopTabsProps,
  // SDK 58 state initially contains only visited routes. Supply placeholders like
  // Expo's built-in Tabs so the catalog can display and open every unvisited tab.
  processDescriptors: appendMissingPlaceholderTabDescriptors,
  processState: appendMissingPlaceholderTabRoutes,
});

// On web Browse's sidebar (`WebBrowseSidebarLayout`, beside every Browse and Search page) lists
// the tabs, so an in-page tab bar would be a duplicate.
const isWeb = Platform.OS === "web";

/**
 * Both native scenes overlap the global bar by its fade inset. Extend that fade through the
 * floating tab bar's remaining lower gutter so content dissolves up to the capsule.
 */
const FLOATING_TAB_PILL_FADE_REACH_PX =
  FLOATING_TAB_BAR_BOTTOM_GAP_PX - PRIMARY_NAVIGATION_STRIP_FADE_INSET_PX;

function renderNullTabBar() {
  return null;
}

// `lazyPlaceholder` is called with NO arguments (expo-router's MaterialTopTabView renders
// `options.lazyPlaceholder?.()`; its option types are loosened to `any`, so a wrong signature
// typechecks). A shared `screenOptions.lazyPlaceholder` destructuring `{ route }` therefore threw
// "Cannot destructure property 'route' of 'e' as it is undefined" and replaced every lazily mounted
// Browse tab with the error screen (staging run 35882257946). Bind it per screen, by name.
function renderBrowseTabLazyPlaceholder(name: string) {
  return name === "primitives" ? (
    <BrowsePrimitivesListSkeleton />
  ) : name === "words" ? (
    <BrowseWordsSkeleton />
  ) : (
    <LevelSurfacesLoadingPage hasFloatingToolbar />
  );
}

export interface BrowseTabsLayoutContentTab {
  route: RoutePath;
  title: string;
}

export function BrowseTabsLayoutContent({
  index,
  tabs,
}: {
  index: string;
  tabs: BrowseTabsLayoutContentTab[];
}) {
  const { theme } = useUniwind();
  const setParentBrowseTab = useSetParentBrowseTab();
  const { navigate } = useBrowseLevelsPageNavigator();
  const navigateToLevels = navigate.mutate;
  const accent = String(useCSSVariable("--stage-mastered") ?? "#f97316");
  // Material Top Tabs defaults inactive tint to a fade of the active color; set muted
  // foreground so only the focused tab is orange.
  const mutedForegroundValue = String(useCSSVariable("--color-muted-foreground") ?? "0 0% 45.1%");
  const inactiveTint = CSS_COLOR_PREFIX_PATTERN.test(mutedForegroundValue)
    ? mutedForegroundValue
    : `hsl(${mutedForegroundValue})`;
  const isBrowseFocused = useIsNavigationFocused();
  usePrimaryNavigationStripFadeReach(FLOATING_TAB_PILL_FADE_REACH_PX, !isWeb && isBrowseFocused);
  // A bottom-placed Admin panel rests above the category tabs instead of covering them.
  usePrimaryNavigationStripFloatingTabs(!isWeb && isBrowseFocused);
  useEffect(() => subscribeBrowseReset(() => navigateToLevels()), [navigateToLevels]);
  if (Platform.OS === "ios") {
    return (
      <NativeTabs
        backBehavior="history"
        disableTransparentOnScrollEdge
        screenListeners={({ route }) => ({
          focus: () => {
            const tab = tabs.find((entry) => path.relative(index, entry.route) === route.name);
            if (tab) {
              setParentBrowseTab(tab.title);
            }
          },
        })}
        tintColor={accent}
        unstable_nativeProps={{ colorScheme: theme === "dark" ? "dark" : "light" }}
      >
        {tabs.map((tab) => {
          const name = path.relative(index, tab.route);
          return (
            <NativeTabs.Trigger
              // Browse lists reserve the native safe-area inset themselves, including a
              // matching upper gutter. Avoid UIKit adding the bar clearance a second time.
              disableAutomaticContentInsets
              key={tab.route}
              name={name}
              // Keep the native glass pill, without the iOS 26 content fade behind it.
              unstable_nativeProps={{ ios: { bottomScrollEdgeEffectHidden: true } }}
            >
              <NativeTabs.Trigger.Label>{tab.title}</NativeTabs.Trigger.Label>
              {name === "levels" ? (
                <NativeTabs.Trigger.Icon sf="list.number" />
              ) : name === "characters" ? (
                <NativeTabs.Trigger.Icon renderingMode="template" src={charactersTabIcon} />
              ) : name === "primitives" ? (
                <NativeTabs.Trigger.Icon renderingMode="template" src={primitivesTabIcon} />
              ) : (
                <NativeTabs.Trigger.Icon renderingMode="template" src={wordsTabIcon} />
              )}
            </NativeTabs.Trigger>
          );
        })}
      </NativeTabs>
    );
  }
  return (
    <View className="flex-1 bg-background">
      <MaterialTopTabs
        // Preserve category-switch history for browser Back.
        backBehavior="history"
        id="characters"
        screenOptions={{
          animationEnabled: false,
          lazy: true,
          sceneStyle: { backgroundColor: "transparent" },
          swipeEnabled: false,
          tabBarActiveTintColor: accent,
          tabBarInactiveTintColor: inactiveTint,
          tabBarIndicatorStyle: { backgroundColor: accent, top: 0 },
          tabBarItemStyle: {
            flexDirection: "row",
            minHeight: 40,
            paddingHorizontal: 0,
            paddingVertical: 4,
          },
          tabBarLabelStyle: {
            fontSize: 11,
            fontWeight: "400",
            margin: 0,
            textTransform: "none",
          },
          tabBarShowIcon: true,
        }}
        tabBar={isWeb ? renderNullTabBar : BrowseMaterialTabBar}
        tabBarPosition="bottom"
      >
        {tabs.map((tab) => {
          const name = path.relative(index, tab.route);
          return (
            <MaterialTopTabs.Screen
              key={tab.route}
              listeners={() => ({
                focus: () => {
                  setParentBrowseTab(tab.title);
                },
              })}
              name={name}
              options={{
                lazyPlaceholder: () => renderBrowseTabLazyPlaceholder(name),
                tabBarIcon: browseLayoutTabBarIcon(name),
                title: tab.title,
              }}
            />
          );
        })}
      </MaterialTopTabs>
    </View>
  );
}
